import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/components/ThemeProvider";
import Index from "./pages/Index";

// Overlays/Toasts sind nicht kritisch für das erste Rendern
const Toaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));
const CookieBanner = lazy(() => import("@/components/CookieBanner").then((m) => ({ default: m.CookieBanner })));
const AccessibilityWidget = lazy(() => import("@/components/AccessibilityWidget").then((m) => ({ default: m.AccessibilityWidget })));

// Alle weiteren Seiten werden erst bei Bedarf geladen (Code-Splitting)
const Verein = lazy(() => import("./pages/Verein"));
const Philosophie = lazy(() => import("./pages/Philosophie"));
const MitgliedWerden = lazy(() => import("./pages/MitgliedWerden"));
const Jahreshauptversammlung2026 = lazy(() => import("./pages/Jahreshauptversammlung2026"));
const KiThinkCube = lazy(() => import("./pages/KiThinkCube"));
const Eventlocation = lazy(() => import("./pages/Eventlocation"));
const Angebote = lazy(() => import("./pages/Angebote"));
const Veranstaltungen = lazy(() => import("./pages/Veranstaltungen"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const Sponsoren = lazy(() => import("./pages/Sponsoren"));
const Fensterpatenschaft = lazy(() => import("./pages/Fensterpatenschaft"));
const SponsorDetail = lazy(() => import("./pages/SponsorDetail"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const Barrierefreiheit = lazy(() => import("./pages/Barrierefreiheit"));
const JazzLounge = lazy(() => import("./pages/JazzLounge"));
const BierTrifftBoerse = lazy(() => import("./pages/BierTrifftBoerse"));
const ZwanzigerJahreAbend = lazy(() => import("./pages/ZwanzigerJahreAbend"));
const Adventsmarkt = lazy(() => import("./pages/Adventsmarkt"));
const AdventsmarktAnmeldung = lazy(() => import("./pages/Adventsmarkt").then((m) => ({ default: m.AdventsmarktAnmeldung })));
const Presse = lazy(() => import("./pages/Presse"));
const Jobs = lazy(() => import("./pages/Jobs"));
const Crowdfunding = lazy(() => import("./pages/Crowdfunding"));
const CommunityVoting = lazy(() => import("./pages/CommunityVoting"));
// Stylesheet des internen Bereichs erst mit den /intern-Routen laden
const internLazy = <T,>(load: () => Promise<{ default: T }>) =>
  lazy(() => import("./intern.css").then(load) as Promise<{ default: any }>);

const InternLogin = internLazy(() => import("./pages/InternLogin"));
const InternRegistrieren = internLazy(() => import("./pages/InternRegistrieren"));
const PasswortSetzen = internLazy(() => import("./pages/PasswortSetzen"));
const InternDashboard = internLazy(() => import("./pages/InternDashboard"));
const InternSpenden = internLazy(() => import("./pages/InternSpenden"));
const InternBuchhaltung = internLazy(() => import("./pages/InternBuchhaltung"));
const InternBelege = internLazy(() => import("./pages/InternBelege"));
const InternBenutzer = internLazy(() => import("./pages/InternBenutzer"));
const InternMietvertraege = internLazy(() => import("./pages/InternMietvertraege"));
const InternMitgliedsantraege = internLazy(() => import("./pages/InternMitgliedsantraege"));
const InternRechnungen = internLazy(() => import("./pages/InternRechnungen"));
const InternAngebote = internLazy(() => import("./pages/InternAngebote"));
const InternFoerderungen = internLazy(() => import("./pages/InternFoerderungen"));
const InternPreisliste = internLazy(() => import("./pages/InternPreisliste"));
const InternKalender = internLazy(() => import("./pages/InternKalender"));
const InternBerichte = internLazy(() => import("./pages/InternBerichte"));
const InternChecklisten = internLazy(() => import("./pages/InternChecklisten"));
const InternPersonalplanung = internLazy(() => import("./pages/InternPersonalplanung"));
const InternIdeenfinder = internLazy(() => import("./pages/InternIdeenfinder"));
const InternSponsoren = internLazy(() => import("./pages/InternSponsoren"));
const InternAnfragen = internLazy(() => import("./pages/InternAnfragen"));
const InternCommunityIdeen = internLazy(() => import("./pages/InternCommunityIdeen"));
const InternWerbematerial = internLazy(() => import("./pages/InternWerbematerial"));
const InternKontakte = internLazy(() => import("./pages/InternKontakte"));
const InternKasse = internLazy(() => import("./pages/InternKasse"));
const InternMonitore = internLazy(() => import("./pages/InternMonitore"));
const MonitorPlayer = lazy(() => import("./pages/MonitorPlayer"));
const NotFound = lazy(() => import("./pages/NotFound"));
const KultmansChat = lazy(() =>
  import("@/components/chat/KultmansChat").then((m) => ({ default: m.KultmansChat }))
);

const queryClient = new QueryClient();

function PublicOverlays() {
  const location = useLocation();
  if (location.pathname.startsWith("/monitor/")) return null;
  return <Suspense fallback={null}><CookieBanner /><AccessibilityWidget /><KultmansChat /></Suspense>;
}

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div
      className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin"
      role="status"
      aria-label="Seite wird geladen"
    />
  </div>
);

const App = () => (
  <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Suspense fallback={null}>
          <Toaster />
          <Sonner />
        </Suspense>
        <BrowserRouter>
          <AuthProvider>
          <ScrollToTop />
           <PublicOverlays />
          <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/verein" element={<Verein />} />
            <Route path="/philosophie" element={<Philosophie />} />
            <Route path="/mitglied-werden" element={<MitgliedWerden />} />
            <Route path="/jahreshauptversammlung-2026" element={<Jahreshauptversammlung2026 />} />
            <Route path="/ki-think-cube" element={<KiThinkCube />} />
            <Route path="/eventlocation" element={<Eventlocation />} />
            <Route path="/angebote" element={<Angebote />} />
            <Route path="/veranstaltungen" element={<Veranstaltungen />} />
            <Route path="/jazz-lounge" element={<JazzLounge />} />
            <Route path="/bier-trifft-boerse" element={<BierTrifftBoerse />} />
            <Route path="/20er-jahre-abend" element={<ZwanzigerJahreAbend />} />
            <Route path="/adventsmarkt" element={<Adventsmarkt />} />
            <Route path="/adventsmarkt/anmeldung" element={<AdventsmarktAnmeldung />} />
            <Route path="/kontakt" element={<Kontakt />} />
            <Route path="/sponsoren" element={<Sponsoren />} />
            <Route path="/fensterpatenschaft" element={<Fensterpatenschaft />} />
            <Route path="/sponsoren/:slug" element={<SponsorDetail />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/barrierefreiheit" element={<Barrierefreiheit />} />
            <Route path="/presse" element={<Presse />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/crowdfunding" element={<Crowdfunding />} />
            <Route path="/community-voting" element={<CommunityVoting />} />
            <Route path="/intern" element={<InternLogin />} />
            <Route path="/intern/registrieren" element={<InternRegistrieren />} />
            <Route path="/passwort-setzen" element={<PasswortSetzen />} />

            <Route path="/intern/login" element={<Navigate to="/intern" replace />} />
            <Route path="/intern/dashboard" element={<InternDashboard />} />
            <Route path="/intern/spenden" element={<InternSpenden />} />
            <Route path="/intern/buchhaltung" element={<InternBuchhaltung />} />
            <Route path="/intern/belege" element={<InternBelege />} />
            <Route path="/intern/benutzer" element={<InternBenutzer />} />
            <Route path="/intern/mietvertraege" element={<InternMietvertraege />} />
            <Route path="/intern/mitgliedsantraege" element={<InternMitgliedsantraege />} />
            <Route path="/intern/rechnungen" element={<InternRechnungen />} />
            <Route path="/intern/angebote" element={<InternAngebote />} />
            <Route path="/intern/foerderungen" element={<InternFoerderungen />} />

            <Route path="/intern/preise" element={<InternPreisliste />} />
            <Route path="/intern/kalender" element={<InternKalender />} />
            <Route path="/intern/berichte" element={<InternBerichte />} />
            <Route path="/intern/checklisten" element={<InternChecklisten />} />
            <Route path="/intern/personalplanung" element={<InternPersonalplanung />} />
            <Route path="/intern/ideenfinder" element={<InternIdeenfinder />} />
            <Route path="/intern/sponsoren" element={<InternSponsoren />} />
            <Route path="/intern/anfragen" element={<InternAnfragen />} />
            <Route path="/intern/community-ideen" element={<InternCommunityIdeen />} />
            <Route path="/intern/werbematerial" element={<InternWerbematerial />} />
            <Route path="/intern/kontakte" element={<InternKontakte />} />
            <Route path="/intern/kasse" element={<InternKasse />} />
            <Route path="/intern/monitore" element={<InternMonitore />} />
            <Route path="/monitor/:slug" element={<MonitorPlayer />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
